import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RecentTrades from '../components/RecentTrades';
import { getSubmissions } from '../services/firebase';
import '../styles/HomePage.css';

const TOP_CHOICE_WALLET = 'klv1355u5mgxl5h8gmxzz9tjt3jpp8h6frxykvvs8zya7xrd3ghhc06qxa9kyq';

const HomePage = () => {
  const [royaltiesWallet, setRoyaltiesWallet] = useState(null);
  const [kongPool, setKongPool] = useState(null);
  const [kongToBeBurned, setKongToBeBurned] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [topCollections, setTopCollections] = useState([]);
  const [topChoiceNfts, setTopChoiceNfts] = useState([]);
  const [topChoiceLoading, setTopChoiceLoading] = useState(false);
  const [launchBoostCollections, setLaunchBoostCollections] = useState([]);
  const [marketStats, setMarketStats] = useState({
    totalVolume: 0,
    totalItems: 0,
    totalOwners: 0
  });
  const [animateStats, setAnimateStats] = useState(false);
  const statsRef = useRef(null);

  // Animated number counter
  const AnimatedNumber = ({ value, suffix = '', decimals = 0 }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
      if (!value) return;
      
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setDisplayValue(value);
          clearInterval(timer);
        } else {
          setDisplayValue(current);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }, [value]);

    return (
      <span>
        {decimals > 0 
          ? displayValue.toFixed(decimals) 
          : Math.floor(displayValue).toLocaleString()}
        {suffix}
      </span>
    );
  };

  const fetchBalances = async () => {
    setIsLoading(true);
    try {
      const [royaltiesResponse, kongPoolResponse, kongBurnResponse] = await Promise.all([
        axios.get('https://api.mainnet.klever.org/v1.0/address/klv19rllvjlz76eph8vlmldmm5rhdyl0kzuhe5c4hp3s4kesyu4dqths8fzhf4'),
        axios.get('https://api.mainnet.klever.org/v1.0/address/klv1nmrp37z578qmkx785yadmjw5v0a253z3de5l4sp5ldn964prn28s0jdfmv'),
        axios.get('https://api.mainnet.klever.org/v1.0/address/klv19eumh6lc27m5sykq3764meqz56kcf85903h8s0pysqgc7xfrpy2sk0yfy4')
      ]);

      setRoyaltiesWallet((royaltiesResponse.data.data.account.balance / 1000000).toFixed(2));
      setKongPool((kongPoolResponse.data.data.account.balance / 1000000).toFixed(2));
      setKongToBeBurned((kongBurnResponse.data.data.account.assets['KONG-LGAJ'].balance / 1000).toFixed(2));
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopCollections = async () => {
    try {
      let allAssets = [];
      const totalPages = 5;

      for (let page = 1; page <= totalPages; page++) {
        const assetsResponse = await axios.get(`https://api.mainnet.klever.org/v1.0/assets/list?page=${page}&limit=100`);
        const assetsData = assetsResponse.data.data.assets.filter(collection => collection.assetType === "NonFungible");
        allAssets = [...allAssets, ...assetsData];
      }

      const marketplaceIds = [
        'd4f2bab340c55fde',
        '7c353c02770da029',
        '2936933ee43db509',
        '417b70c0eb7a33cb',
        '116056b257d9f6d5',
        'aa68ad853a09988c',
        'd2a04fe890a6edda'
      ];

      const statsPromises = marketplaceIds.map(id =>
        axios.get(`https://api.mainnet.klever.org/v1.0/marketplaces/${id}/stats`)
      );

      const statsResponses = await Promise.all(statsPromises);
      let combinedStatsData = [];

      statsResponses.forEach(response => {
        combinedStatsData = [...combinedStatsData, ...response.data.data.stats];
      });

      let mergedCollections = allAssets.map(asset => {
        let totalVolume = 0;
        let totalItems = 0;
        let totalOwners = 0;
        let floorPrices = [];
        let royalties = null;

        combinedStatsData.forEach(stat => {
          if (stat.assetId === asset.assetId) {
            totalVolume += stat.volumeTotal || 0;
            totalItems += stat.items || 0;
            totalOwners += stat.owners || 0;
            if (stat.floor && stat.floor > 0) {
              floorPrices.push(stat.floor);
            }
          }
        });

        const combinedFloorPrice = floorPrices.length > 0 ? Math.min(...floorPrices) : 0;

        if (asset.royalties && asset.royalties.marketPercentage) {
          royalties = (asset.royalties.marketPercentage / 100).toString();
          royalties = royalties.includes('.') ? royalties.replace(/\.00$/, '') : royalties;
        }

        return {
          ...asset,
          volumeTotal: totalVolume,
          totalItems,
          totalOwners,
          floorPrice: combinedFloorPrice,
          royalties,
          logo: asset.logo,
          name: asset.name
        };
      });

      mergedCollections = mergedCollections.sort((a, b) => b.volumeTotal - a.volumeTotal);
      
      // Calculate market stats
      const stats = {
        totalVolume: mergedCollections.reduce((acc, col) => acc + col.volumeTotal, 0),
        totalItems: mergedCollections.reduce((acc, col) => acc + col.totalItems, 0),
        totalOwners: mergedCollections.reduce((acc, col) => acc + col.totalOwners, 0)
      };
      setMarketStats(stats);

      setTopCollections(mergedCollections.slice(0, 10));
    } catch (error) {
      console.error('Error fetching top collections:', error);
    }
  };

  // Fetch Top Choice NFTs (cryptographically verified via transaction metadata)
  const fetchTopChoiceNfts = async () => {
    setTopChoiceLoading(true);
    try {
      const featuredNfts = [];
      const marketplaceId = '417b70c0eb7a33cb';
      const topChoiceWallet = 'klv1355u5mgxl5h8gmxzz9tjt3jpp8h6frxykvvs8zya7xrd3ghhc06qxa9kyq';

      console.log('🔍 Scanning for Top Choice listings...');

      // Query SellContract transactions - these have the TOP_CHOICE metadata
      let page = 1;
      let hasMore = true;
      const maxPages = 5;

      while (hasMore && page <= maxPages && featuredNfts.length < 6) {
        try {
          // Get SellContract transactions (type 18)
          const response = await axios.get(
            `https://api.mainnet.klever.org/v1.0/transaction/list?type=18&page=${page}&limit=100`
          );

          const transactions = response.data?.data?.transactions || [];
          console.log(`📄 Page ${page}: Scanning ${transactions.length} transactions`);

          if (transactions.length === 0) {
            hasMore = false;
            break;
          }

          // Debug: show first 3 with data on page 1
          if (page === 1) {
            const withData = transactions.filter(t => t.data).slice(0, 3);
            console.log(`🔍 Found ${withData.length} txs with data on page 1`);
            withData.forEach((t, idx) => {
              try {
                console.log(`  🔍 TX${idx + 1} data type:`, typeof t.data);
                console.log(`  🔍 TX${idx + 1} is array?:`, Array.isArray(t.data));

                let decoded = '';

                if (Array.isArray(t.data)) {
                  // Data is array - could be: [hexString], [[bytes]], or [bytes]
                  console.log(`  🔍 TX${idx + 1} array length:`, t.data.length);
                  console.log(`  🔍 TX${idx + 1} first element type:`, typeof t.data[0]);

                  if (t.data.length === 1 && typeof t.data[0] === 'string') {
                    // Array with single hex string: ["544f505f..."]
                    console.log(`  🔍 TX${idx + 1} hex string in array, length:`, t.data[0].length);
                    const hexString = t.data[0];
                    const bytes = [];
                    for (let i = 0; i < hexString.length; i += 2) {
                      bytes.push(parseInt(hexString.substring(i, i + 2), 16));
                    }
                    decoded = String.fromCharCode(...bytes);
                  } else if (t.data.length > 0 && Array.isArray(t.data[0])) {
                    // Nested array of bytes: [[84, 79, 80, ...]]
                    console.log(`  🔍 TX${idx + 1} nested array, first element length:`, t.data[0].length);
                    decoded = String.fromCharCode(...t.data[0]);
                  } else {
                    // Flat array of bytes: [84, 79, 80, ...]
                    console.log(`  🔍 TX${idx + 1} flat array of bytes`);
                    decoded = String.fromCharCode(...t.data);
                  }
                } else if (typeof t.data === 'string') {
                  // Data is hex string
                  console.log(`  🔍 TX${idx + 1} string length:`, t.data.length);
                  const bytes = [];
                  for (let i = 0; i < t.data.length; i += 2) {
                    bytes.push(parseInt(t.data.substring(i, i + 2), 16));
                  }
                  decoded = String.fromCharCode(...bytes);
                } else {
                  console.log(`  ⚠️ TX${idx + 1} unexpected data format:`, t.data);
                }

                const preview = decoded.length > 60 ? decoded.substring(0, 60) + '...' : decoded;
                console.log(`  📋 TX${idx + 1} decoded:`, preview);
              } catch (e) {
                console.log(`  ❌ TX${idx + 1} decode error:`, e.message);
              }
            });
          }

          for (const tx of transactions) {
            if (featuredNfts.length >= 6) break;
            if (tx.status !== 'success') continue;

            // Check if this is a SellContract for our marketplace
            const contract = tx.contract?.[0];
            if (!contract || contract.type !== 18) continue;

            // Check marketplace ID (note: API uses 'marketplaceID' with capital ID)
            const txMarketplaceId = contract.parameter?.marketplaceID || contract.parameter?.marketplaceId;
            if (txMarketplaceId !== marketplaceId) continue;

            // Check for TOP_CHOICE metadata in the transaction
            const txData = tx.data;
            if (!txData) continue;

            // Decode data to text (handle multiple formats)
            let decodedString = '';
            try {
              if (Array.isArray(txData)) {
                // Data is array - could be: [hexString], [[bytes]], or [bytes]
                if (txData.length === 1 && typeof txData[0] === 'string') {
                  // Array with single hex string: ["544f505f..."]
                  const hexString = txData[0];
                  const bytes = [];
                  for (let i = 0; i < hexString.length; i += 2) {
                    bytes.push(parseInt(hexString.substring(i, i + 2), 16));
                  }
                  decodedString = String.fromCharCode(...bytes);
                } else if (txData.length > 0 && Array.isArray(txData[0])) {
                  // Nested array of bytes: [[84, 79, 80, ...]]
                  decodedString = String.fromCharCode(...txData[0]);
                } else {
                  // Flat array of bytes: [84, 79, 80, ...]
                  decodedString = String.fromCharCode(...txData);
                }
              } else if (typeof txData === 'string') {
                // Data is hex string
                const bytes = [];
                for (let i = 0; i < txData.length; i += 2) {
                  bytes.push(parseInt(txData.substring(i, i + 2), 16));
                }
                decodedString = String.fromCharCode(...bytes);
              } else {
                continue;
              }
            } catch (e) {
              continue;
            }

            // Must start with TOP_CHOICE
            if (!decodedString.startsWith('TOP_CHOICE:')) continue;

            console.log('✓ Valid TOP_CHOICE marker found:', decodedString.substring(0, 100));

            // Format: "TOP_CHOICE:paymentHash:assetId"
            const parts = decodedString.split(':');
            console.log('  → Split into', parts.length, 'parts');

            if (parts.length !== 3) {
              console.log('⚠️ Invalid TOP_CHOICE format (expected 3 parts):', decodedString.substring(0, 80));
              continue;
            }

            const paymentHash = parts[1];
            const assetId = parts[2];

            console.log('  → Payment hash:', paymentHash.substring(0, 16) + '...');
            console.log('  → Asset ID:', assetId);

            if (!assetId || !assetId.includes('/')) {
              console.log('⚠️ Invalid asset ID format:', assetId);
              continue;
            }

            console.log('🎯 Found Top Choice listing:', assetId);

            // Verify the 50 KLV payment exists
            try {
              const paymentResponse = await axios.get(
                `https://api.mainnet.klever.org/v1.0/transaction/${paymentHash}`
              );

              const paymentTx = paymentResponse.data?.data?.transaction;
              if (!paymentTx || paymentTx.status !== 'success') {
                console.log('⚠️ Payment transaction not found or failed');
                continue;
              }

              const paymentContract = paymentTx.contract?.[0];
              if (paymentContract?.type !== 0) {
                console.log('⚠️ Payment is not a TransferContract');
                continue;
              }

              const amount = paymentContract.parameter?.amount || 0;
              const receiver = paymentContract.parameter?.toAddress || '';

              if (amount !== 50000000 || receiver !== topChoiceWallet) {
                console.log('⚠️ Invalid payment amount or receiver');
                continue;
              }

              console.log('✅ Verified 50 KLV payment');
            } catch (err) {
              console.log('⚠️ Error verifying payment:', err.message);
              continue;
            }

            // Fetch NFT data and verify listing is still active
            try {
              const [collectionId, nftNonce] = assetId.split('/');
              const currentTime = Math.floor(Date.now() / 1000);

              // Check market orders to verify listing is still active and not expired
              let activeOrder = null;
              try {
                const orderResponse = await axios.get(
                  `https://api.mainnet.klever.org/v1.0/assets/${assetId}/market-orders`
                );

                const orders = orderResponse.data?.data?.orders || {};
                const ordersList = Array.isArray(orders) ? orders : Object.values(orders);

                // Find active order that matches our marketplace and is not expired
                activeOrder = ordersList.find(order => {
                  // Must be from our marketplace
                  if (order.marketplaceId !== marketplaceId) return false;

                  // Must be active (orderStatus 0 = active, 1 = filled/sold)
                  if (order.orderStatus !== 0) return false;

                  // Must not be expired (check endTime if available)
                  if (order.endTime && order.endTime < currentTime) return false;

                  return true;
                });

                if (!activeOrder) {
                  console.log('⚠️ Listing expired, sold, or no longer active - skipping');
                  continue;
                }

                console.log('✅ Found active order, endTime:', activeOrder.endTime ? new Date(activeOrder.endTime * 1000).toISOString() : 'N/A');
              } catch (marketOrderError) {
                // If market orders API fails, check the original listing transaction's endTime
                console.log('⚠️ Could not verify market order status via API');

                // Check the endTime from the original SellContract
                const sellEndTime = contract.parameter?.endTime;
                if (sellEndTime && sellEndTime < currentTime) {
                  console.log('⚠️ Listing expired based on original transaction, skipping');
                  continue;
                }
              }

              // Fetch collection metadata to build NFT image URL (same approach as RecentTrades)
              let imageUrl = '';
              let nftName = `#${nftNonce}`;

              try {
                const collectionRes = await axios.get(
                  `https://api.mainnet.klever.org/v1.0/assets/${collectionId}`
                );
                const collectionData = collectionRes.data?.data?.asset || {};

                nftName = collectionData.name ? `${collectionData.name} #${nftNonce}` : `#${nftNonce}`;

                // Try to get metadata URI from collection
                const metadataObj = collectionData.uris?.find(uri => uri.key.toLowerCase() === 'metadata');
                let metadataUri = metadataObj?.value;

                if (metadataUri) {
                  // Build metadata URL
                  if (metadataUri === "QmNaa2KQ6NkjjESpPHEnAow9hivnsAkq2Gd6R26cHG28Er") {
                    metadataUri = `https://ipfs.io/ipfs/${metadataUri}/${nftNonce}.json`;
                  } else {
                    if (metadataUri.includes("klever-mint.mypinata.cloud")) {
                      metadataUri = metadataUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
                    }
                    metadataUri = `${metadataUri}/${nftNonce}`;
                  }

                  // Fetch NFT metadata
                  const metadataRes = await axios.get(metadataUri);
                  imageUrl = metadataRes.data?.image || '';

                  // Fix image URLs
                  if (imageUrl) {
                    if (imageUrl.includes("www.lovemonsternft.com")) {
                      const imageName = imageUrl.split('/').pop();
                      imageUrl = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
                    } else if (imageUrl.includes("klever-mint.mypinata.cloud")) {
                      imageUrl = imageUrl.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
                    }
                  }
                }

                // Fallback to collection logo if no image found
                if (!imageUrl && collectionData.logo) {
                  imageUrl = collectionData.logo;
                  if (imageUrl.includes("klever-mint.mypinata.cloud")) {
                    imageUrl = imageUrl.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
                  }
                }
              } catch (metadataError) {
                console.log('⚠️ Could not fetch NFT image, using fallback');
              }

              // Replace Pinata URLs with IPFS
              if (imageUrl && imageUrl.includes("klever-mint.mypinata.cloud")) {
                imageUrl = imageUrl.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
              }

              featuredNfts.push({
                assetId,
                collectionId,
                nftNonce,
                name: nftName,
                image: imageUrl,
                timestamp: tx.timestamp,
                price: activeOrder?.price || 0
              });

              console.log('✅ Added to Top Choice:', nftName);
            } catch (err) {
              console.error('Error verifying/fetching NFT', assetId, ':', err);
              console.error('  → Error details:', {
                message: err.message,
                status: err.response?.status,
                url: err.config?.url
              });
            }
          }

          page++;
        } catch (err) {
          console.error('Error fetching page', page, ':', err);
          hasMore = false;
        }
      }

      console.log(`✅ Scanned ${page - 1} pages, found ${featuredNfts.length} verified Top Choice listings`);

      // Sort by timestamp (most recent first)
      featuredNfts.sort((a, b) => b.timestamp - a.timestamp);

      console.log('⭐ Top Choice NFTs found:', featuredNfts.length);
      setTopChoiceNfts(featuredNfts);
    } catch (error) {
      console.error('❌ Error fetching Top Choice NFTs:', error);
    } finally {
      setTopChoiceLoading(false);
    }
  };

  const handleImageError = (event) => {
    const src = event.target.src;
    if (
      src.startsWith("https://klever-mint.mypinata.cloud/ipfs/") &&
      src !== "https://klever-mint.mypinata.cloud/ipfs/QmNwWcvPmfNzPa4JQppj8mXcaeM8BA2yA5VAjVWYLzTT7a"
    ) {
      event.target.src = src.replace(
        "https://klever-mint.mypinata.cloud/ipfs/",
        "https://ipfs.io/ipfs/"
      );
    }
  };

  // Intersection Observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimateStats(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  // Load Launch Boost collections from Firebase (approved submissions)
  const loadLaunchBoostCollections = async () => {
    try {
      const submissions = await getSubmissions();
      const approvedBoosts = submissions.filter(
        sub => sub.service === 'launch_boost' && sub.status === 'approved'
      );

      const boostCollections = approvedBoosts.map(sub => ({
        id: sub.id,
        assetId: sub.collectionId || sub.materials?.collection || 'UNKNOWN',
        name: sub.materials?.description?.slice(0, 50) || sub.collectionId || 'Boosted Collection',
        bannerImage: sub.images?.banner?.data,
        logoImage: sub.images?.logo?.data,
        twitter: sub.materials?.twitter,
        telegram: sub.materials?.telegram,
        wallet: sub.wallet,
        approvedAt: sub.approvedAt || sub.submittedAt
      }));

      setLaunchBoostCollections(boostCollections);
    } catch (error) {
      console.error('Error loading Launch Boost collections:', error);
    }
  };

  useEffect(() => {
    fetchBalances();
    fetchTopCollections();
    fetchTopChoiceNfts();
    loadLaunchBoostCollections();
    const interval = setInterval(() => {
      fetchBalances();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="home-container">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          {topCollections.slice(0, 10).map((collection, index) => (
            <img
              key={index}
              src={collection.logo}
              alt={`${collection.name} logo`}
              className={`hero-logo hero-logo-${index}`}
              onError={handleImageError}
            />
          ))}
          <div className="grid-overlay"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            Powered by Klever Chain
          </div>
          <h1 className="hero-title">
            Discover Extraordinary
            <span className="title-highlight"> NFTs</span>
          </h1>
          <p className="hero-subtitle">
            The ultimate marketplace for digital collectibles. Trade with confidence,
            collect with passion.
          </p>
          
          <div className="hero-actions">
            <Link to="/collections" className="btn-primary">
              <span>Explore Collections</span>
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link to="/creator" className="btn-secondary">
              <span>Create NFT</span>
            </Link>
          </div>

          {/* Social Icons */}
          <div className="hero-social">
            <a href="https://X.com/FOMO_Apes" target="_blank" rel="noopener noreferrer" className="social-icon-link" title="Twitter">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://t.me/fomoape_nft" target="_blank" rel="noopener noreferrer" className="social-icon-link" title="Telegram">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
            </a>
          </div>

          {/* Quick Market Stats */}
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="stat-value">
                <AnimatedNumber value={marketStats.totalVolume} suffix=" KLV" decimals={0} />
              </div>
              <div className="stat-label">Total Volume</div>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <div className="stat-value">
                <AnimatedNumber value={marketStats.totalItems} suffix="+" decimals={0} />
              </div>
              <div className="stat-label">NFTs Listed</div>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <div className="stat-value">
                <AnimatedNumber value={marketStats.totalOwners} suffix="+" decimals={0} />
              </div>
              <div className="stat-label">Collectors</div>
            </div>
          </div>
        </div>
      </div>

      <div className="content-wrapper">
        {/* Top Collections Section */}
        <section className="top-collections-section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="title-icon">🔥</span>
              Most traded Collections
            </h2>
            <Link to="/collections" className="view-all-link">
              View All
              <svg className="link-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          
          <div className="collections-grid">
            {topCollections.length > 0 ? (
              topCollections.slice(0, 6).map((collection, index) => (
                <Link 
                  to={`/collections/${collection.assetId}`} 
                  key={index} 
                  className="collection-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="collection-rank">#{index + 1}</div>
                  <div className="collection-image-wrapper">
                    <img
                      src={collection.logo}
                      alt={collection.name}
                      className="collection-image"
                      onError={handleImageError}
                    />
                    <div className="image-overlay"></div>
                  </div>
                  <div className="collection-details">
                    <h3 className="collection-name">{collection.name}</h3>
                    <div className="collection-stats-row">
                      <div className="collection-stat">
                        <span className="stat-label">Volume</span>
                        <span className="stat-value">
                          {collection.volumeTotal ? `${(collection.volumeTotal / 1000000).toFixed(1)}M KLV` : '0 KLV'}
                        </span>
                      </div>
                      {collection.floorPrice > 0 && (
                        <div className="collection-stat">
                          <span className="stat-label">Floor</span>
                          <span className="stat-value">
                            {(collection.floorPrice / 1000000).toFixed(2)} KLV
                          </span>
                        </div>
                      )}
                    </div>
                    {collection.royalties && (
                      <div className="collection-royalty">
                        <svg className="royalty-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                        </svg>
                        {collection.royalties}% Royalty
                      </div>
                    )}
                  </div>
                </Link>
              ))
            ) : (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading collections...</p>
              </div>
            )}
          </div>
        </section>

        {/* Top Choice Featured NFTs */}
        {topChoiceNfts.length > 0 && (
          <section className="top-choice-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="title-icon">⭐</span>
                Top Choice
              </h2>
              <span className="featured-badge">Featured Listings</span>
            </div>

            <div className="top-choice-grid">
              {topChoiceNfts.map((nft, index) => (
                <Link
                  to={`/collections/${nft.collectionId}/${nft.nftNonce}`}
                  key={nft.assetId}
                  className="top-choice-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="top-choice-badge">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                    </svg>
                  </div>
                  <div className="top-choice-image">
                    {nft.image ? (
                      <img src={nft.image} alt={nft.name} onError={handleImageError} />
                    ) : (
                      <div className="top-choice-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="top-choice-info">
                    <h3 className="top-choice-name">{nft.name}</h3>
                    <span className="top-choice-id">#{nft.nftNonce}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Launch Boost Featured Collections */}
        {launchBoostCollections.length > 0 && (
          <section className="launch-boost-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="title-icon">🚀</span>
                Launch Boost
              </h2>
              <span className="featured-badge boost-badge">Promoted</span>
            </div>

            <div className="launch-boost-grid">
              {launchBoostCollections.map((collection, index) => (
                <Link
                  to={`/collections/${collection.assetId}`}
                  key={collection.id}
                  className="launch-boost-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {collection.bannerImage && (
                    <div className="boost-banner">
                      <img src={collection.bannerImage} alt={collection.name} />
                      <div className="boost-banner-overlay"></div>
                    </div>
                  )}
                  <div className="boost-content">
                    {collection.logoImage && (
                      <div className="boost-logo">
                        <img src={collection.logoImage} alt={collection.name} />
                      </div>
                    )}
                    <div className="boost-info">
                      <h3 className="boost-name">{collection.name}</h3>
                      <span className="boost-id">{collection.assetId}</span>
                    </div>
                    <div className="boost-badge-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M13.13 22.19L11.5 18.36C13.07 17.78 14.54 17 15.9 16.09L13.13 22.19M5.64 12.5L1.81 10.87L7.91 8.1C7 9.46 6.22 10.93 5.64 12.5M21.61 2.39C21.61 2.39 16.66 .269 11 5.93C8.81 8.12 7.5 10.53 6.65 12.64C6.37 13.39 6.56 14.21 7.11 14.77L9.24 16.89C9.79 17.45 10.61 17.63 11.36 17.35C13.5 16.53 15.88 15.19 18.07 13C23.73 7.34 21.61 2.39 21.61 2.39M14.54 9.46C13.76 8.68 13.76 7.41 14.54 6.63S16.59 5.85 17.37 6.63C18.14 7.41 18.15 8.68 17.37 9.46C16.59 10.24 15.32 10.24 14.54 9.46M8.88 16.53L7.47 15.12L8.88 16.53M6.24 22L9.88 18.36C9.54 18.27 9.21 18.12 8.91 17.91L4.83 22H6.24M2 22H3.41L8.18 17.24L6.76 15.83L2 20.59V22M2 19.17L6.09 15.09C5.88 14.79 5.73 14.47 5.64 14.12L2 17.76V19.17Z" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Recent Trades Section */}
        <section className="recent-trades-section">
          <RecentTrades limit={10} showTitle={true} />
        </section>

        {/* KONG Stats Section */}
        <section className="stats-section" ref={statsRef}>
          <div className="section-header">
            <h2 className="section-title">
              <span className="title-icon">💎</span>
              KONG Ecosystem
            </h2>
          </div>
          
          <div className="stats-grid">
            <div className={`stat-card ${animateStats ? 'animate' : ''}`} style={{ animationDelay: '0s' }}>
              <div className="stat-icon royalties-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="stat-title">Royalties Wallet</h3>
              <p className="stat-value">
                {isLoading ? (
                  <span className="skeleton-text">Loading...</span>
                ) : (
                  <>
                    <AnimatedNumber value={parseFloat(royaltiesWallet)} suffix="" decimals={2} />
                    <span className="stat-unit"> KLV</span>
                  </>
                )}
              </p>
              <p className="stat-description">
                Distributed to FOMO APE NFT holders at 250,000 KLV
              </p>
              <div className="stat-progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${Math.min((parseFloat(royaltiesWallet || 0) / 250000) * 100, 100)}%` }}
                ></div>
              </div>
            </div>

            <div className={`stat-card ${animateStats ? 'animate' : ''}`} style={{ animationDelay: '0.1s' }}>
              <div className="stat-icon pool-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="stat-title">$KONG Pool</h3>
              <p className="stat-value">
                {isLoading ? (
                  <span className="skeleton-text">Loading...</span>
                ) : (
                  <>
                    <AnimatedNumber value={parseFloat(kongPool)} suffix="" decimals={2} />
                    <span className="stat-unit"> KLV</span>
                  </>
                )}
              </p>
              <p className="stat-description">
                Weekly distribution to stakers every Sunday
              </p>
              <div className="stat-badge">
                <span className="badge-pulse"></span>
                Active Staking
              </div>
            </div>

            <div className={`stat-card ${animateStats ? 'animate' : ''}`} style={{ animationDelay: '0.2s' }}>
              <div className="stat-icon burn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                </svg>
              </div>
              <h3 className="stat-title">$KONG to be Burned</h3>
              <p className="stat-value">
                {isLoading ? (
                  <span className="skeleton-text">Loading...</span>
                ) : (
                  <>
                    <AnimatedNumber value={parseFloat(kongToBeBurned)} suffix="" decimals={2} />
                    <span className="stat-unit"> KONG</span>
                  </>
                )}
              </p>
              <p className="stat-description">
                Burned from swap fees every Sunday
              </p>
              <div className="stat-badge burn-badge">
                <svg className="burn-flame" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C12 2 7 6 7 12C7 15.31 9.69 18 13 18C16.31 18 19 15.31 19 12C19 6 14 2 14 2C14 2 13 4 12 6C11 4 12 2 12 2Z" />
                </svg>
                Deflationary
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="cta-section">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Collection?</h2>
            <p className="cta-description">
              Join thousands of collectors trading on the most advanced NFT marketplace on Klever Chain
            </p>
            <div className="cta-actions">
              <Link to="/collections" className="btn-primary">
                <span>Browse Marketplace</span>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;